import React, { useEffect, useState } from "react";
import { useAdminAuth } from "./AdminAuthContext";
import { useToast } from "./ToastProvider";
import { supabase } from "../lib/supabase";
import AdminMFACard from "./AdminMFACard";

const KEY_LABELS = {
  "public.store_name": { label: "Store name", type: "string" },
  "public.shipping_rate_inr": { label: "Shipping rate (INR)", type: "number" },
  "public.free_shipping_min": { label: "Free shipping above (INR)", type: "number" },
  "public.gst_rate_pct": { label: "GST rate (%)", type: "number" },
  "public.currency_default": { label: "Default currency", type: "string" },
  "private.gstin": { label: "GSTIN", type: "string" },
  "private.owner_email": { label: "Owner notification email", type: "string" },
};

export default function AdminSettings() {
  const { apiFetch, admin } = useAdminAuth();
  const toast = useToast();
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [newAdmin, setNewAdmin] = useState("");

  // Change-password local state
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  useEffect(() => {
    apiFetch(`/admin/settings`).then(({ data }) => setValues(data || {})).catch(() => {});
  }, [apiFetch]);

  const set = (k, raw) => {
    const t = KEY_LABELS[k]?.type;
    const v = t === "number" ? (raw === "" ? "" : Number(raw)) : raw;
    setValues({ ...values, [k]: v });
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiFetch(`/admin/settings`, {
        method: "PATCH",
        data: { items: values },
        headers: { "Content-Type": "application/json" },
      });
      toast("Settings saved");
    } catch (e) {
      toast("Save failed", "err");
    } finally {
      setSaving(false);
    }
  };

  const addAdmin = async () => {
    if (!newAdmin) return;
    try {
      await apiFetch(`/admin/users`, {
        method: "POST",
        data: { email: newAdmin, send_welcome: true },
        headers: { "Content-Type": "application/json" },
      });
      toast(`Admin invite sent to ${newAdmin}`);
      setNewAdmin("");
    } catch (e) {
      toast(e?.response?.data?.detail || "Failed", "err");
    }
  };

  const [seeding, setSeeding] = useState(false);
  const seedFake = async () => {
    if (!window.confirm("Insert 10 fake paid orders + customers? Re-running replaces previous fakes only. Real orders untouched.")) return;
    setSeeding(true);
    try {
      const { data } = await apiFetch(`/admin/dev/seed-fake-orders?count=10`, { method: "POST" });
      toast(`Seeded ${data.orders_inserted} orders / ${data.customers_inserted} customers`);
    } catch (e) {
      toast(e?.response?.data?.detail || "Seed failed", "err");
    } finally {
      setSeeding(false);
    }
  };
  const clearFake = async () => {
    if (!window.confirm("Delete all fake orders/customers? Real orders untouched.")) return;
    setSeeding(true);
    try {
      await apiFetch(`/admin/dev/seed-fake-orders`, { method: "DELETE" });
      toast("Fake data cleared");
    } catch (e) {
      toast("Clear failed", "err");
    } finally {
      setSeeding(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwNew.length < 12) {
      toast("New password must be at least 12 chars", "err");
      return;
    }
    if (pwNew !== pwConfirm) {
      toast("Passwords do not match", "err");
      return;
    }
    if (pwNew === pwCurrent) {
      toast("New password must differ from current", "err");
      return;
    }
    setPwBusy(true);
    try {
      // 1. Verify the current password by re-authenticating. Supabase has no
      //    built-in "verify current password" call, so we re-sign-in.
      const { error: reauthErr } = await supabase.auth.signInWithPassword({
        email: admin?.email,
        password: pwCurrent,
      });
      if (reauthErr) {
        toast("Current password is incorrect", "err");
        setPwBusy(false);
        return;
      }
      // 2. Update to the new one.
      const { error: updateErr } = await supabase.auth.updateUser({
        password: pwNew,
      });
      if (updateErr) {
        toast(updateErr.message || "Update failed", "err");
        setPwBusy(false);
        return;
      }
      setPwCurrent("");
      setPwNew("");
      setPwConfirm("");
      toast("Password updated. Use the new one next time you sign in.");
    } catch (err) {
      toast(err?.message || "Password change failed", "err");
    } finally {
      setPwBusy(false);
    }
  };

  const razorpayConnected = (process.env.REACT_APP_RAZORPAY_KEY_ID || "").startsWith("rzp_") &&
    !(process.env.REACT_APP_RAZORPAY_KEY_ID || "").includes("placeholder");

  return (
    <>
      <div className="adm-page-head">
        <div>
          <h1 className="adm-h1">Settings</h1>
          <div className="adm-h1-sub">Store configuration</div>
        </div>
      </div>

      <div className="adm-card" style={{ marginBottom: 16 }}>
        <div className="adm-card-h"><div className="adm-card-title">Store</div></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {Object.entries(KEY_LABELS).map(([k, meta]) => {
            const raw = values[k];
            const display = raw === undefined || raw === null ? "" :
              (typeof raw === "string" && raw.startsWith('"') ? raw.slice(1, -1) : String(raw));
            return (
              <div key={k}>
                <label className="adm-label">{meta.label}</label>
                <input
                  className="adm-input"
                  type={meta.type === "number" ? "number" : "text"}
                  value={display}
                  onChange={(e) => set(k, e.target.value)}
                />
              </div>
            );
          })}
        </div>
        <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
          <button className="adm-btn" onClick={save} disabled={saving}>{saving ? "Saving..." : "Save"}</button>
        </div>
      </div>

      <div className="adm-card" style={{ marginBottom: 16 }}>
        <div className="adm-card-h"><div className="adm-card-title">Integrations</div></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Integration name="Razorpay" connected={razorpayConnected} sub={razorpayConnected ? "Live key configured" : "Placeholder key. Drop real rzp_test_* in /app/backend/.env"} />
          <Integration name="Resend" connected={true} sub="Email key configured. Verify a sending domain to deliver mail." />
          <Integration name="Supabase" connected={true} sub="Database, auth, storage active." />
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <AdminMFACard />
      </div>

      <form className="adm-card" onSubmit={changePassword} style={{ marginBottom: 16 }}>
        <div className="adm-card-h">
          <div className="adm-card-title">Change password</div>
          <div className="adm-card-sub">Signed in as {admin?.email}. Minimum 12 characters.</div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
          <div>
            <label className="adm-label">Current password</label>
            <input
              data-testid="pw-current"
              className="adm-input"
              type="password"
              autoComplete="current-password"
              value={pwCurrent}
              onChange={(e) => setPwCurrent(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="adm-label">New password</label>
            <input
              data-testid="pw-new"
              className="adm-input"
              type="password"
              autoComplete="new-password"
              value={pwNew}
              onChange={(e) => setPwNew(e.target.value)}
              minLength={12}
              required
            />
          </div>
          <div>
            <label className="adm-label">Confirm new password</label>
            <input
              data-testid="pw-confirm"
              className="adm-input"
              type="password"
              autoComplete="new-password"
              value={pwConfirm}
              onChange={(e) => setPwConfirm(e.target.value)}
              minLength={12}
              required
            />
          </div>
        </div>
        <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
          <button data-testid="pw-submit" className="adm-btn" type="submit" disabled={pwBusy || !pwCurrent || !pwNew || !pwConfirm}>
            {pwBusy ? "Updating..." : "Update password"}
          </button>
        </div>
      </form>

      <div className="adm-card">
        <div className="adm-card-h"><div className="adm-card-title">Add admin user</div><div className="adm-card-sub">Sends a Supabase invite + welcome email</div></div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <input data-testid="admin-invite-email" className="adm-input" placeholder="ops@damageculture.in" value={newAdmin} onChange={(e) => setNewAdmin(e.target.value)} style={{ maxWidth: 360 }} />
          <button data-testid="admin-invite-btn" className="adm-btn" onClick={addAdmin} disabled={!newAdmin}>Invite admin</button>
        </div>
      </div>

      {/* Developer tools only show in non-production environments (preview, localhost). */}
      {!/^(www\.)?damageculture\.com$/i.test(typeof window !== "undefined" ? window.location.hostname : "") && (
        <div className="adm-card" style={{ marginTop: 16 }}>
          <div className="adm-card-h">
            <div className="adm-card-title">Developer tools</div>
            <div className="adm-card-sub">Seed/clear synthetic data for dashboards. Hidden on production. Real orders untouched.</div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button data-testid="seed-fake-orders-btn" className="adm-btn" onClick={seedFake} disabled={seeding}>
              {seeding ? "Working..." : "Seed 10 fake orders"}
            </button>
            <button data-testid="clear-fake-orders-btn" className="adm-btn adm-btn-ghost" onClick={clearFake} disabled={seeding}>
              Clear fake orders
            </button>
          </div>
        </div>
      )}
    </>
  );
}

const Integration = ({ name, connected, sub }) => (
  <div style={{ border: "1px solid #26262C", borderRadius: 10, padding: 14 }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div style={{ fontSize: 14, fontWeight: 600 }}>{name}</div>
      <span className={`adm-pill ${connected ? "delivered" : "pending"}`}><span className="dot" /> {connected ? "Connected" : "Not configured"}</span>
    </div>
    <div style={{ fontSize: 12, color: "#8B8B93", marginTop: 6 }}>{sub}</div>
  </div>
);
